import { useState, type ReactNode } from 'react';
import SmartImage from './SmartImage';

interface Props {
  src: string;
  alt: string;
  fallback?: string;
  className?: string;
  frameClassName?: string;
  /** Used only until the browser has measured the real image. */
  initialRatio?: number;
  children?: ReactNode;
}

/**
 * A measured image frame for cards and listing heroes.
 *
 * The frame starts at a stable landscape ratio, then switches to the image's
 * natural ratio as soon as it loads. That means portrait, square, panoramic,
 * and ordinary landscape listing images each get a proportionate card rather
 * than being forced through one 16:9 crop.
 */
export default function AspectRatioImage({
  src,
  alt,
  fallback,
  className = '',
  frameClassName = '',
  initialRatio = 16 / 9,
  children,
}: Props) {
  const [ratio, setRatio] = useState(initialRatio);

  return (
    <div
      className={`relative overflow-hidden bg-elevated-light dark:bg-elevated ${frameClassName}`}
      style={{ aspectRatio: ratio }}
      >
      <SmartImage
        src={src}
        alt={alt}
        fallback={fallback}
        mode="natural"
        onAspectRatio={setRatio}
        className={`absolute inset-0 w-full h-full ${className}`}
      />
      {children}
    </div>
  );
}
